import { useCallback, useEffect, useMemo, useState } from "react";
import { adminApi, API_BASE_URL } from "../lib/api";
import type { AdminSection } from "../types";
import { InlineError, LoadingState } from "../components/States";
import { PageHeader } from "../components/PageHeader";

interface Metric {
  label: string;
  value: string;
}

function humanize(key: string): string {
  return key
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .replace(/^./, (letter) => letter.toUpperCase());
}

function displayValue(value: unknown): string {
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (typeof value === "number") return new Intl.NumberFormat("en").format(value);
  if (typeof value === "string") {
    const timestamp = Date.parse(value);
    if (
      Number.isFinite(timestamp) &&
      /\d{4}-\d{2}-\d{2}|T\d{2}:\d{2}/.test(value)
    ) {
      return new Intl.DateTimeFormat("en", {
        dateStyle: "medium",
        timeStyle: "short"
      }).format(new Date(timestamp));
    }
    return value;
  }
  if (value === null || value === undefined) return "—";
  return "";
}

function collectMetrics(data: Record<string, unknown>): Metric[] {
  const preferred = [
    "newInquiries",
    "inquiryCount",
    "totalInquiries",
    "draftStatus",
    "publishedAt",
    "mediaCount"
  ];
  const entries = Object.entries(data).filter(([, value]) =>
    ["string", "number", "boolean"].includes(typeof value)
  );
  entries.sort(([a], [b]) => {
    const aIndex = preferred.indexOf(a);
    const bIndex = preferred.indexOf(b);
    return (aIndex < 0 ? 99 : aIndex) - (bIndex < 0 ? 99 : bIndex);
  });
  return entries.slice(0, 6).map(([key, value]) => ({
    label: humanize(key),
    value: displayValue(value)
  }));
}

export function OverviewPage({
  onNavigate
}: {
  onNavigate: (section: AdminSection) => void;
}) {
  const [data, setData] = useState<Record<string, unknown> | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [checkedAt, setCheckedAt] = useState<Date | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      setData(await adminApi.dashboard());
      setCheckedAt(new Date());
    } catch (requestError) {
      setError(
        requestError instanceof Error
          ? requestError.message
          : "The dashboard could not be loaded."
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const metrics = useMemo(() => (data ? collectMetrics(data) : []), [data]);

  return (
    <>
      <PageHeader
        eyebrow="Website operations"
        title="Overview"
        description="A concise view of content, requests, and the media library."
        actions={
          <button type="button" className="button" onClick={() => void load()}>
            Refresh
          </button>
        }
      />

      {loading ? (
        <LoadingState label="Loading overview" />
      ) : error ? (
        <InlineError message={error} retry={() => void load()} />
      ) : (
        <>
          {metrics.length > 0 && (
            <section className="metric-grid" aria-label="Website status">
              {metrics.map((metric) => (
                <article className="metric" key={metric.label}>
                  <p>{metric.label}</p>
                  <strong>{metric.value}</strong>
                </article>
              ))}
            </section>
          )}

          <div className="overview-grid">
            <section className="panel">
              <div className="panel__heading">
                <div>
                  <p className="eyebrow">Current state</p>
                  <h2>API status</h2>
                </div>
                <span className="status-badge status-badge--success">Live</span>
              </div>
              <p className="connection-copy">
                The private admin API responded successfully. Content changes
                remain in draft until they are explicitly published.
              </p>
              <dl className="detail-list">
                <div>
                  <dt>Endpoint</dt>
                  <dd>{new URL(API_BASE_URL).hostname}</dd>
                </div>
                <div>
                  <dt>Last checked</dt>
                  <dd>
                    {checkedAt
                      ? new Intl.DateTimeFormat("en", {
                          dateStyle: "medium",
                          timeStyle: "short"
                        }).format(checkedAt)
                      : "—"}
                  </dd>
                </div>
                <div>
                  <dt>Access key storage</dt>
                  <dd>This browser session only</dd>
                </div>
              </dl>
            </section>

            <section className="panel">
              <div className="panel__heading">
                <div>
                  <p className="eyebrow">Shortcuts</p>
                  <h2>Common tasks</h2>
                </div>
              </div>
              <div className="shortcut-list">
                <button type="button" onClick={() => onNavigate("content")}>
                  <span>Edit website copy</span>
                  <span aria-hidden="true">→</span>
                </button>
                <button type="button" onClick={() => onNavigate("inquiries")}>
                  <span>Review new inquiries</span>
                  <span aria-hidden="true">→</span>
                </button>
                <button type="button" onClick={() => onNavigate("media")}>
                  <span>Add a website image</span>
                  <span aria-hidden="true">→</span>
                </button>
                <button type="button" onClick={() => onNavigate("settings")}>
                  <span>Check contact details</span>
                  <span aria-hidden="true">→</span>
                </button>
              </div>
            </section>
          </div>
        </>
      )}
    </>
  );
}
